import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const SCALE = 2
const RECEIPT_WIDTH_MM = 80
const MAX_PAGE_HEIGHT_MM = 280
const RENDER_SETTLE_MS = 250

function waitForImage(img) {
  if (img.complete && img.naturalWidth > 0) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true })
    img.addEventListener('error', resolve, { once: true })
  })
}

/** Aguarda imagens e scripts assíncronos (ex.: QR Code do cupom Focus). */
async function waitForEmbeddedContent(root) {
  if (!root) return

  const images = root.querySelectorAll('img')
  await Promise.all(Array.from(images).map(waitForImage))

  await new Promise((resolve) => setTimeout(resolve, RENDER_SETTLE_MS))
  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

function mountHtmlInHiddenIframe(html) {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe')
    iframe.className = 'nfce-pdf-export-root'
    iframe.setAttribute('title', 'Exportação NFC-e')
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:320px;height:1200px;border:0;visibility:hidden;'
    iframe.srcdoc = html

    let settled = false

    const finish = async () => {
      if (settled) return
      settled = true

      try {
        const doc = iframe.contentDocument
        const body = doc?.body
        if (!body) {
          throw new Error('Conteúdo do cupom NFC-e não disponível para exportação')
        }
        await waitForEmbeddedContent(body)
        resolve({ element: body, iframe })
      } catch (err) {
        reject(err)
      }
    }

    iframe.addEventListener('load', () => {
      finish().catch(reject)
    })
    iframe.addEventListener('error', () => {
      if (!settled) {
        settled = true
        reject(new Error('Não foi possível renderizar o cupom NFC-e'))
      }
    })

    document.body.appendChild(iframe)

    if (iframe.contentDocument?.readyState === 'complete') {
      finish().catch(reject)
    }
  })
}

function getCaptureTarget(element) {
  const doc = element.ownerDocument
  if (element.tagName === 'BODY' && doc?.documentElement) {
    return doc.documentElement
  }
  return element
}

/**
 * Gera PDF a partir do HTML do cupom NFC-e (Focus não expõe danfe.pdf para modelo 65).
 * @param {HTMLElement|string} htmlOrElement
 * @param {string} filename
 */
export async function exportNfceHtmlToPdf(htmlOrElement, filename) {
  let element = null
  let cleanup = null

  if (typeof htmlOrElement === 'string') {
    const mounted = await mountHtmlInHiddenIframe(htmlOrElement)
    element = mounted.element
    cleanup = () => {
      if (mounted.iframe.parentNode) {
        mounted.iframe.parentNode.removeChild(mounted.iframe)
      }
    }
  } else if (
    htmlOrElement &&
    typeof htmlOrElement === 'object' &&
    htmlOrElement.nodeType === Node.ELEMENT_NODE
  ) {
    element = htmlOrElement
    await waitForEmbeddedContent(element)
  } else {
    throw new Error('Conteúdo do cupom NFC-e não disponível para exportação')
  }

  const captureWindow = element.ownerDocument?.defaultView || window
  const captureTarget = getCaptureTarget(element)

  try {
    const canvas = await html2canvas(captureTarget, {
      scale: SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#FFFFFF',
      logging: false,
      window: captureWindow,
      width: captureTarget.scrollWidth || 320,
      height: captureTarget.scrollHeight,
      windowWidth: captureTarget.scrollWidth || 320
    })

    const imgWidthMm = RECEIPT_WIDTH_MM
    const pageHeightPx = (MAX_PAGE_HEIGHT_MM * canvas.width) / imgWidthMm

    const pdf = new jsPDF('p', 'mm', [imgWidthMm, MAX_PAGE_HEIGHT_MM])
    let sourceY = 0
    let pageIndex = 0

    while (sourceY < canvas.height - 1) {
      const sliceHeightPx = Math.min(pageHeightPx, canvas.height - sourceY)

      if (pageIndex > 0) {
        pdf.addPage([imgWidthMm, MAX_PAGE_HEIGHT_MM])
      }

      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = sliceHeightPx
      const ctx = pageCanvas.getContext('2d')
      ctx.drawImage(
        canvas,
        0, sourceY, canvas.width, sliceHeightPx,
        0, 0, canvas.width, sliceHeightPx
      )

      const sliceHeightMm = (sliceHeightPx * imgWidthMm) / canvas.width
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.92)
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidthMm, sliceHeightMm)

      sourceY += sliceHeightPx
      pageIndex += 1
    }

    if (pageIndex === 0) {
      throw new Error('Não foi possível renderizar o cupom NFC-e')
    }

    pdf.save(filename || 'NFC-e.pdf')

    return { pageCount: pageIndex }
  } finally {
    if (cleanup) cleanup()
  }
}
